"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, RotateCcw, Building2, CircleCheck as CheckCircle2, ArrowUpRight, ChartBar as BarChart3, Wrench, SlidersHorizontal, Sparkles, Quote } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function CaseResults() {
  const shouldReduceMotion = useReducedMotion();
  const keyMetrics = [{
    metric: "+193%",
    label: "Growth in Monthly Leads",
    subtext: "From 14 to 41 verified monthly inquiries by Month 4",
    badge: "Verified Growth",
    icon: TrendingUp
  }, {
    metric: "312",
    label: "Monthly Website Visits",
    subtext: "Consistent targeted organic visitors seeking local cleaning services",
    badge: "Organic Traffic",
    icon: BarChart3
  }, {
    metric: "Reactivated",
    label: "Repeat Bookings",
    subtext: "Former clients inactive for 12+ months re-engaged and booked",
    badge: "Client Retention",
    icon: RotateCcw
  }, {
    metric: "Expanded",
    label: "Market Reach",
    subtext: "Scaled from small offices into corporate centers & residential housekeeping",
    badge: "Diversification",
    icon: Building2
  }];
  const pillars = [{
    title: "Technical & On-Page Site Cleanup",
    description: "Conducted a complete technical and on-page overhaul to eliminate site speed bottlenecks, streamline mobile user experience, and structure service pages for frictionless navigation.",
    highlights: ["Site speed optimization and mobile UX refinements across all landing pages", "Clear site hierarchy and logical internal linking structure", "Streamlined call-to-action paths designed for fast quote requests"],
    icon: Wrench
  }, {
    title: "Iterative Monthly Performance Reviews",
    description: "Maintained hands-on monthly reviews analyzing user query intent and ranking movements, deliberately pruning underperforming keywords to focus authority on high-converting search terms.",
    highlights: ["Pruning non-converting and irrelevant search queries", "Reallocating crawl budget and topical authority to high-intent terms", "Continuous on-page refinement driven by actual inquiry data"],
    icon: SlidersHorizontal
  }, {
    title: "Keyword Expansion & Service Diversification",
    description: "Engineered strategic keyword clusters that broadened the company's scope, enabling direct bids for corporate office contracts while capturing demand in residential housekeeping.",
    highlights: ["Targeted corporate offices and commercial business centers", "Dedicated keyword structure enabling entry into residential housekeeping", "Geo-targeted search intent capture without cannibalizing existing rankings"],
    icon: Sparkles
  }];
  const leadProgression = [{
    label: "Baseline (Month 1)",
    value: 14,
    color: "bg-muted"
  }, {
    label: "Verified Outcome (Month 4)",
    value: 41,
    color: "bg-primary"
  }];
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="casestudyresults" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mb-14 md:mb-20">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Verified SEO Case Study
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
            Delivering Verified Growth Through Targeted Organic Search
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Here is how I structured, executed, and refined an organic search strategy that took a local specialist from 14 monthly inquiries to 41 consistent, high-intent client leads by Month 4.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {keyMetrics.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.label} data-index={i} initial="hidden" whileInView="show" viewport={{
            once: true,
            margin: "-50px"
          }} variants={cardVariants}>
                <Card className="h-full border border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] bg-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="size-10 rounded-md bg-secondary flex items-center justify-center text-primary">
                        <Icon className="size-5" />
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {item.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-3xl sm:text-4xl font-bold text-primary tracking-tight font-default">
                      {item.metric}
                    </CardTitle>
                    <CardDescription className="text-sm font-semibold text-foreground mt-1">
                      {item.label}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {item.subtext}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          <div className="lg:col-span-5 space-y-6">
            <Card className="border border-border bg-card shadow-sm">
              <CardHeader>
                <div className="flex items-center gap-2 text-primary font-medium text-sm">
                  <BarChart3 className="size-4" />
                  <span>Verified Lead Flow Progression</span>
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold font-default text-foreground">
                  Monthly Inquiries Tracking
                </CardTitle>
                <CardDescription>
                  Verified inbound commercial and domestic quote inquiries recorded before and after Month 4 optimization.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-5">
                  {leadProgression.map((prog, i) => <div key={prog.label} data-index={i} className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs sm:text-sm">
                        <span className="font-medium text-foreground">{prog.label}</span>
                        <span className="font-bold text-primary">{prog.value} leads/mo</span>
                      </div>
                      <div className="w-full h-3 bg-muted rounded-md overflow-hidden">
                        <div className={`h-full ${prog.color} transition-[width] duration-500 rounded-md`} style={{
                      width: `${prog.value / 41 * 100}%`
                    }} />
                      </div>
                    </div>)}
                </div>

                <Separator />

                <div className="bg-muted/60 rounded-lg p-4 space-y-2 border border-border/50">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    Key Business Outcome
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Scaling organic visibility into corporate facilities contracts increased average deal values, while reactivating repeat bookings and opening residential housekeeping diversified their recurring revenue base.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <h3 className="text-base font-bold text-foreground font-default mb-2">
                Work Directly With Me
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-4">
                No account managers or junior handoffs. Every strategy and technical implementation is handled directly by me.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild variant="default" size="sm" className="w-full sm:w-auto">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                  <Link to="/CaseStudiesPage">
                    View All Case Studies
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <div className="mb-2">
              <h3 className="text-xl sm:text-2xl font-bold font-default text-foreground">
                How I Engineered This Growth
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                A focused execution strategy combining technical cleanup, continuous query pruning, and strategic keyword expansion.
              </p>
            </div>

            <div className="space-y-4">
              {pillars.map((pillar, i) => {
              const PillarIcon = pillar.icon;
              return <motion.div key={pillar.title} data-index={i} initial="hidden" whileInView="show" viewport={{
                once: true,
                margin: "-50px"
              }} variants={cardVariants}>
                    <Card className="border border-border bg-card shadow-sm hover:border-primary/40 transition-[border-color,box-shadow]">
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-3">
                          <div className="size-9 rounded-md bg-secondary flex items-center justify-center text-primary shrink-0">
                            <PillarIcon className="size-5" />
                          </div>
                          <div>
                            <CardTitle className="text-lg font-bold text-foreground font-default">
                              {pillar.title}
                            </CardTitle>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                        <ul className="space-y-2 pt-1">
                          {pillar.highlights.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                              <span>{point}</span>
                            </li>)}
                        </ul>
                      </CardContent>
                    </Card>
                  </motion.div>;
            })}
            </div>
          </div>
        </div>

        <div className="mb-16">
          <div className="relative rounded-2xl bg-card border border-border p-6 sm:p-8 lg:p-10 shadow-sm overflow-hidden">
            <div className="absolute -top-4 -right-4 text-secondary/60 pointer-events-none select-none">
              <Quote className="size-32" />
            </div>
            <div className="relative z-10 max-w-4xl space-y-4">
              <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider">
                <Quote className="size-4" />
                <span>Consultant Commentary</span>
              </div>
              <blockquote className="text-base sm:text-lg lg:text-xl text-foreground font-medium leading-relaxed italic">
                &ldquo;By combining technical site cleanup with disciplined monthly keyword pruning, we converted organic search into a predictable commercial pipeline. Expanding our search coverage not only secured lucrative contracts with corporate offices and business centers, but also allowed the company to successfully launch and scale residential housekeeping as a profitable new service line.&rdquo;
              </blockquote>
              <div className="pt-2 flex items-center gap-3">
                <div className="size-10 rounded-lg bg-primary text-primary-foreground font-bold flex items-center justify-center text-sm">
                  YE
                </div>
                <div>
                  <div className="text-sm font-bold text-foreground">Yassine Essaadi</div>
                  <div className="text-xs text-muted-foreground">Independent SEO Consultant &middot; YE Consultancy</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-secondary/70 border border-border p-6 sm:p-8 lg:p-10 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-bold font-default text-foreground">
              Ready to grow your search visibility and qualified inbound leads?
            </h3>
            <p className="text-sm text-muted-foreground">
              Get in touch directly with me to discuss an actionable, honest SEO strategy tailored to your market.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>Book a Consultation</span>
                <ArrowUpRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/ServicesPage">
                Explore Services
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}